import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, AUTH_EXPIRED_EVENT, api } from '../lib/api'

interface AuthState {
  /** Username from the session (Keycloak `preferred_username` typically). Null if not authenticated. */
  user: string | null
  /** True when the user has the "admin" realm role in Keycloak. */
  isAdmin: boolean
  /** True until the initial /api/auth/me probe completes. */
  loading: boolean
  /** True when the API doesn't have auth configured server-side (e.g. before Keycloak env is in place). */
  authDisabled: boolean
  logout: () => Promise<void>
}

const AuthCtx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [user, setUser] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [authDisabled, setAuthDisabled] = useState(false)

  // Initial probe.
  useEffect(() => {
    let cancelled = false
    api.authMe().then(
      (r) => {
        if (cancelled) return
        setUser(r.user)
        setIsAdmin(!!r.isAdmin)
      },
      (err) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 503) setAuthDisabled(true)
        setUser(null)
        setIsAdmin(false)
      },
    ).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // When any /api/* call returns 401 mid-session, drop the user back to the login page.
  useEffect(() => {
    const onExpired = () => {
      setUser(null)
      setIsAdmin(false)
      qc.clear()
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired)
  }, [qc])

  const logout = useCallback(async () => {
    try { await api.authLogout() } catch {/* local state is the source of truth */}
    setUser(null)
    setIsAdmin(false)
    qc.clear()
    // Strip any ?auth_error=... from the URL so we land on a clean login screen.
    if (window.location.search) window.history.replaceState({}, '', window.location.pathname)
  }, [qc])

  const value = useMemo<AuthState>(
    () => ({ user, isAdmin, loading, authDisabled, logout }),
    [user, isAdmin, loading, authDisabled, logout],
  )

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth() outside AuthProvider')
  return ctx
}
